import dayjs from 'dayjs';

import { DEPTH_AVAILABILITY_LAYERS } from 'other/map/layerValues';
import { GEOSERVER_WMTS } from 'other/config';
import { getElevation } from 'other/map/layerValueHelpers';
import { isTouchDevice } from 'other/helpers';

import {
  EMapLayer,
  EMarkerCategory,
  EMiniCardPanel,
  TDraggableMapEntity,
  TFarmArea,
  TFarmLocation,
  TFarmLocationExt,
  TIdName,
  TLayerPointLocation,
  TLayerPointValue,
  TLayerValues,
  TMapEntityState,
  TVesselLocation,
  TVesselLocationExt
} from 'types';
import { TMapEntities } from './mapEntitiesModel';

/**
 *
 */
export function doEntitiesDedupe<T extends TIdName>(entities: T[]): T[] {
  if (!Array.isArray(entities) || entities.length === 0) return entities;

  const map = new Map();
  entities.forEach((ent) => ent && map.set(ent.id, ent));

  return Array.from(map.values());
}

/**
 *
 */
export function dedupeEntities(entities: {
  [EMarkerCategory.CAGE]: TFarmLocation[] | TFarmLocationExt[];
  [EMarkerCategory.VESSEL]: TVesselLocation[] | TVesselLocationExt[];
}): {
  [EMarkerCategory.CAGE]: TFarmLocation[] | TFarmLocationExt[];
  [EMarkerCategory.VESSEL]: TVesselLocation[] | TVesselLocationExt[];
} {
  return {
    [EMarkerCategory.CAGE]: doEntitiesDedupe(entities[EMarkerCategory.CAGE]),
    [EMarkerCategory.VESSEL]: doEntitiesDedupe(entities[EMarkerCategory.VESSEL])
  };
}

/**
 *
 */
export const DEFAULT_MINI_CARD_PANEL = isTouchDevice()
  ? EMiniCardPanel.RIGHT
  : EMiniCardPanel.LEFT;

/**/
export type SimpleEntity = {
  id?: number;
  providerId?: number;
};

/**/
export function makeFarmFromArea(
  areas: TFarmArea[],
  farmId: number
): TFarmLocation {
  const area = areas.find(
    (a: TFarmArea) => a.properties.fishfarm.id === farmId
  );
  if (!area) return null;

  return {
    ...area?.properties.fishfarm,
    latitude: area.geometry.coordinates[0][0][1],
    longitude: area.geometry.coordinates[0][0][0]
  } as any;
}

/**/
export function categoryContainsItem(
  entities: TMapEntities,
  targetItem: SimpleEntity,
  type: EMarkerCategory
): boolean {
  const items = entities[type] as [any];
  const item = items.find(
    (e) =>
      (e.id && targetItem.id && e.id === targetItem.id) ||
      (e.providerId &&
        targetItem.providerId &&
        e.providerId === targetItem.providerId)
  );
  return !!item;
}

/**
 * Layers and layer specific data section.
 */

/**
 *
 */
const CNS_LAYERS = {
  [EMapLayer.CHLOROPHYLL]: 'cns:ChlorophyllData',
  [EMapLayer.ICE_COVERAGE]: 'cns:IceCoverageData',
  [EMapLayer.PHYTOPLANKTON]: 'cns:PlanktonData',
  [EMapLayer.SALINITY]: 'cns:SalinityData',
  [EMapLayer.SEATEMP]: 'cns:SeaTemperatureData',
  [EMapLayer.SURFACETEMP]: 'cns:SeaTemperatureData',
  [EMapLayer.UPWELLING]: 'cns:UpwellingData',
  [EMapLayer.ZOOPLANKTON]: 'cns:ZooplanktonData'
};

/**
 *
 */
export function fetchLocationValue(
  { coords, ij, latitude, longitude }: TLayerPointLocation,
  layer: EMapLayer,
  layerValuesMap: Record<EMapLayer, TLayerValues>,
  date
): Promise<TLayerPointValue> {
  const { depth, maxRange, range } = layerValuesMap[layer];

  let url =
    GEOSERVER_WMTS +
    '?VERSION=1.0.0' +
    `&LAYER=${CNS_LAYERS[layer]}` +
    '&STYLE=' +
    '&TILEMATRIX=EPSG:3857:' +
    coords.z +
    '&TILEMATRIXSET=EPSG:3857&SERVICE=WMTS&FORMAT=image/png&SERVICE=WMTS' +
    '&REQUEST=GetFeatureInfo&INFOFORMAT=application/json' +
    `&TileCol=${coords.x}&TileRow=${coords.y}` +
    `&I=${ij.i}&J=${ij.j}` +
    `&TIME=${dayjs(date).format('YYYY-MM-DD')}`;

  if (DEPTH_AVAILABILITY_LAYERS.includes(layer)) {
    url += `&ELEVATION=${getElevation(depth, layer)}`;
  }

  return fetch(url)
    .then((r) => r.json())
    .then(({ features, timeStamp }): TLayerPointValue => {
      const value = getValue(features, layer);
      if (!isValueValid(value, range, maxRange, layer)) return;

      return {
        latitude: latitude,
        longitude: longitude,
        timeStamp: timeStamp,
        title: getTitle(layer),
        units: getUnits(layer),
        value: value
      };
    });
}

/**
 *
 */
function getValue(features, layer: EMapLayer): number {
  const v = Object.values(features[0]?.properties)[0] as number;
  if (typeof v !== 'number') return v;

  return layer === EMapLayer.UPWELLING ? v * 1e6 : v;
}

/**
 *
 */
function getTitle(layer: EMapLayer): string {
  switch (layer) {
    case EMapLayer.CHLOROPHYLL:
      return 'Chlorophyll';
    case EMapLayer.PHYTOPLANKTON:
      return 'Phytoplankton';
    case EMapLayer.SALINITY:
      return 'Salinity';
    case EMapLayer.SEATEMP:
      return 'Sub sea temperature';
    case EMapLayer.SURFACETEMP:
      return 'Surface temperature';
    case EMapLayer.UPWELLING:
      return 'Upwelling activity';
    case EMapLayer.ZOOPLANKTON:
      return 'Zooplankton';
  }
}

/**
 *
 */
function getUnits(layer: EMapLayer): string {
  switch (layer) {
    case EMapLayer.CHLOROPHYLL:
      return 'mg/m³';
    case EMapLayer.ZOOPLANKTON:
    case EMapLayer.PHYTOPLANKTON:
      return 'mmol/m&sup3;';
    case EMapLayer.SALINITY:
      return 'g/kg';
    case EMapLayer.SEATEMP:
    case EMapLayer.SURFACETEMP:
      return '°C';
    case EMapLayer.ICE_COVERAGE:
      return '';
    case EMapLayer.UPWELLING:
      return 'µm/s';
  }
}

/**/
const ABSOLUTE_MAX_RANGE = {
  MIN: -1e4,
  MAX: 1e4
};

/**/
function isValueValid(
  value: unknown,
  range: [number, number],
  maxRange: [number, number],
  layer: EMapLayer
): boolean {
  let [min, max] = range;
  if (typeof value !== 'number') return false;

  if (layer === EMapLayer.UPWELLING) {
    min = range[0] === maxRange[0] ? ABSOLUTE_MAX_RANGE.MIN : min;
    max = range[1] === maxRange[1] ? ABSOLUTE_MAX_RANGE.MAX : max;
  }

  if (range[0] > maxRange[0] && value < min) return false;

  if (range[1] < maxRange[1] && value > max) return false;

  return value < ABSOLUTE_MAX_RANGE.MAX && value > ABSOLUTE_MAX_RANGE.MIN;
}

/**
 *
 */
export function updateEntityUIState(
  entityId: number,
  list: TDraggableMapEntity[],
  uiState: Partial<TMapEntityState>
): TDraggableMapEntity[] {
  return list.map((item: TDraggableMapEntity) => {
    if (item.id !== entityId) return item;
    const stateUpdate = {
      ...(item.uiState || null),
      ...uiState
    };
    return {
      ...item,
      uiState: stateUpdate
    };
  });
}

/* Tracks coloring */
export const colorArray: string[] = [
  '#FF0000',
  '#00FF00',
  '#0000FF',
  '#FFFF00',
  '#00FFFF',
  '#FF00FF',
  '#3366E6',
  '#999966',
  '#99FF99',
  '#B34D4D',
  '#80B300',
  '#809900',
  '#E6B3B3',
  '#6680B3',
  '#66991A',
  '#FF99E6',
  '#CCFF1A',
  '#FF1A66',
  '#E6331A',
  '#33FFCC',
  '#66994D',
  '#B366CC',
  '#4D8000',
  '#B33300',
  '#CC80CC',
  '#66664D',
  '#991AFF',
  '#E666FF',
  '#4DB3FF',
  '#1AB399',
  '#E666B3',
  '#33991A',
  '#CC9999',
  '#B3B31A',
  '#00E680',
  '#4D8066',
  '#809980',
  '#E6FF80',
  '#1AFF33',
  '#999933',
  '#FF3380',
  '#CCCC00',
  '#66E64D',
  '#4D80CC',
  '#9900B3',
  '#E64D66',
  '#4DB380',
  '#FF4D4D',
  '#99E6E6',
  '#6666FF'
];
